import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Button, FormControl, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, TextField, Card } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';

import TopNavActions from './TopNavActions';
import ConfirmExitDialog from './ConfirmExitDialog';

interface EligibilityCriteriaLanguagesViewProps {
  enrollment: any;
  onPageChange: (page: string) => void;
}

const availableSpeakingLanguages = ["English", "French", "Spanish", "Other"];
const additionalSpeakingLanguages = ["None", "English", "French", "Spanish", "Other"];
const localeMap: { [key: string]: Locale } = { 'en': enUS, 'es-419': es };

// Format a Date object as 'yyyy-mm-dd'
const formatDateToYYYYMMDD = (d: Date): string => {
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse a 'yyyy-mm-dd' string into a Date object
const parseYYYYMMDDToDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const EligibilityCriteriaLanguagesView: React.FC<EligibilityCriteriaLanguagesViewProps> = ({ enrollment, onPageChange }) => {
  const [selectedSpeakingLanguage, setSelectedSpeakingLanguage] = useState(enrollment?.languageInformation?.primaryLanguage ?? enrollment?.language ?? '');
  const [otherPrimaryLanguage, setOtherPrimaryLanguage] = useState('');
  const [selectedAdditionalSpeakingLanguages, setSelectedAdditionalSpeakingLanguages] = useState(['None']);
  const [otherSpeakingLanguage, setOtherSpeakingLanguage] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [filteredAdditionalSpeakingLanguages, setFilteredAdditionalSpeakingLanguages] = useState<string[]>([]);
  const [openExitDialog, setOpenExitDialog] = useState(false);
  const { t, i18n } = useTranslation('enrollment');
  const currentLocale = localeMap[i18n.language] || enUS;

  useEffect(() => {
    setSelectedSpeakingLanguage(enrollment?.languageInformation?.primaryLanguage ?? enrollment?.language ?? '');
    setOtherPrimaryLanguage(enrollment?.languageInformation?.specifiedPrimaryLanguage ?? '');
    setSelectedAdditionalSpeakingLanguages(
      enrollment?.languageInformation?.additionalLanguages?.length > 0
        ? enrollment.languageInformation.additionalLanguages
        : ['None']
    );
    setOtherSpeakingLanguage(enrollment?.languageInformation?.specifiedAdditionalLanguages ?? '');
    setDateOfBirth(enrollment?.subjectInformation?.dateOfBirth ?? '');
    setFilteredAdditionalSpeakingLanguages(
      additionalSpeakingLanguages.filter(x => x !== (enrollment?.languageInformation?.primaryLanguage ?? enrollment.language))
    );
  }, [enrollment]);

  // Handle primary language change
  const handleSpeakingLanguageChange = (lang: string) => {
    setSelectedSpeakingLanguage(lang);
    if (lang !== 'Other') {
      setOtherPrimaryLanguage('');
      setFilteredAdditionalSpeakingLanguages(
        additionalSpeakingLanguages.filter(x => !x.includes(lang))
      );
    } else {
      setFilteredAdditionalSpeakingLanguages([...additionalSpeakingLanguages]);
    }
    setSelectedAdditionalSpeakingLanguages(prev => prev.filter(x => x !== lang));
  };

  // Handle additional languages change
  const handleAdditionalLanguagesChange = (lang: string) => {
    let updated: string[];
    if (lang === 'None') {
      updated = ['None'];
    } else {
      updated = selectedAdditionalSpeakingLanguages.filter(x => x !== 'None');
      if (selectedAdditionalSpeakingLanguages.includes(lang)) {
        updated = updated.filter(x => x !== lang);
      } else {
        updated = [...updated, lang];
      }
    }
    setSelectedAdditionalSpeakingLanguages(updated);
    if (!updated.includes('Other')) {
      setOtherSpeakingLanguage('');
    }
  };

  // Validate date
  const dateFilter = (dateStr: string) => {
    if (!dateStr) return true;
    return new Date(dateStr) <= new Date();
  };

  // Navigation handlers
  const handleNext = () => {
    enrollment.languageInformation = {
      primaryLanguage: selectedSpeakingLanguage,
      specifiedPrimaryLanguage: otherPrimaryLanguage,
      additionalLanguages: selectedAdditionalSpeakingLanguages,
      specifiedAdditionalLanguages: otherSpeakingLanguage
    };
    enrollment.subjectInformation = {
      dateOfBirth: dateOfBirth ?? '',
      firstName: enrollment?.subjectInformation?.firstName ?? '',
      lastName: enrollment?.subjectInformation?.lastName ?? '',
      phoneNumber: enrollment?.subjectInformation?.phoneNumber ?? '',
      emailAddress: enrollment?.subjectInformation?.emailAddress ?? ''
    };
    onPageChange('eligibility-criteria-conditions-view');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const isNextDisabled =
    !dateOfBirth ||
    !dateFilter(dateOfBirth) ||
    selectedSpeakingLanguage === '' ||
    (selectedSpeakingLanguage === 'Other' && otherPrimaryLanguage.trim() === '') ||
    (selectedAdditionalSpeakingLanguages.includes('Other') && otherSpeakingLanguage.trim() === '');

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />

      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1} >{t("Tell us about yourself")}</Typography>
      {/* Primary Language */}

      <Card style={{padding: '0 1rem 1rem', marginBottom: '1rem'}}>
        <FormControl fullWidth margin="normal">
          <Typography variant='h6' component='label' htmlFor="primary-language">{t("Select your primary language")}</Typography>
          <Select
            id="primary-language"
            value={selectedSpeakingLanguage}
            onChange={e => handleSpeakingLanguageChange(e.target.value as string)}
            name="language"
            inputProps={{ "id":"primary-language", style: { display: 'none' } }}
          >
            {availableSpeakingLanguages.map(language => (
              <MenuItem key={language} value={language}>{t(language)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedSpeakingLanguage === 'Other' && (
          <div>
            <FormControl fullWidth margin="normal">
              <Typography variant='h6'>{t("Other")}, {t("Please specify")}: </Typography>
              <TextField
                fullWidth
                value={otherPrimaryLanguage}
                onChange={e => setOtherPrimaryLanguage(e.target.value)}
              />
            </FormControl>
          </div>
        )}
      </Card>

      {/* Additional Languages */}
      <Card style={{padding: '0 1rem 1rem', marginBottom: '1rem'}}>
        <FormControl fullWidth margin="normal" component="fieldset">
          <Typography variant="h6" component='legend'>
            {t("Do you speak any additional language(s) fluently (similar to a native speaker)?")}
          </Typography>
          <FormGroup id="additional-languages">
            {filteredAdditionalSpeakingLanguages.map(language => (
              <FormControlLabel
                key={language}
                control={
                  <Checkbox
                    checked={selectedAdditionalSpeakingLanguages.includes(language)}
                    onChange={() => handleAdditionalLanguagesChange(language)}
                    disabled={language === selectedSpeakingLanguage}
                  />
                }
                label={t(language)}
              />
            ))}
          </FormGroup>
        </FormControl>
        {selectedAdditionalSpeakingLanguages.includes('Other') && (
          <div>
            <FormControl fullWidth margin="normal">
              <Typography variant='h6'>{t("Other")}, {t("Please specify")}: </Typography>
              <TextField
                fullWidth
                value={otherSpeakingLanguage}
                onChange={e => setOtherSpeakingLanguage(e.target.value)}
                />
            </FormControl>
          </div>
        )}
      </Card>
      {/* Date of Birth */}

      <Card style={{padding: '0 1rem 1rem', marginBottom: '1rem'}}>
        <FormControl fullWidth margin="normal">
          <Typography variant='h6' component='label' htmlFor="date-of-birth">{t("What is your date of birth?")}</Typography>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={currentLocale}
          >
            <DatePicker
              value={dateOfBirth ? parseYYYYMMDDToDate(dateOfBirth) : null}
              onChange={(date) => setDateOfBirth(date ? formatDateToYYYYMMDD(date) : '')}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  id: 'date-of-birth',
                  inputProps: { readOnly: true }
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Card>
      <Button variant="contained" onClick={handleNext} disabled={isNextDisabled} style={{ marginTop: 32, marginBottom: 32 }}>
        {t("Next")}
      </Button>
    </React.Fragment>
  );
};

export default EligibilityCriteriaLanguagesView;
